/**
 * 図書館検索お気に入り用の Zustand ストア
 *
 * バックエンド `/library-favorites` と同期する。
 * - `byIsbn`: 正規化 ISBN をキーとしたマップ (高速な `isFavorite` 判定用)
 * - `order`: 表示用の並び順 (登録新しい順)
 * - `toggle`: 楽観的更新 (成功時は API レスポンスで確定、失敗時はロールバック)
 */

import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import {
  addLibraryFavorite,
  listLibraryFavorites,
  receiveLibraryPickups,
  removeLibraryFavorite,
  updateLibraryFavoriteStatus,
} from '@/api/services/library-favorite'
import { registerSyncListener } from '@/api/sync-events'
import type {
  LibraryFavorite,
  LibraryFavoriteInput,
  LibraryFavoriteStatus,
  ReservedLibrary,
} from '@/api/types/library-favorite'

import { normalizeIsbn } from './useLibrarySearchStore'

type LibraryFavoritesState = {
  /** 正規化 ISBN → お気に入り書誌 */
  byIsbn: Record<string, LibraryFavorite>
  /** 表示順 (新しい順の正規化 ISBN 配列) */
  order: string[]
  loaded: boolean
  loading: boolean
  error: string | null

  loadFavorites: () => Promise<void>
  isFavorite: (isbn: string | null | undefined) => boolean
  /**
   * ISBN で toggle する。
   * - 未登録: `input` を使って POST
   * - 登録済み: DELETE
   * ISBN が正規化できない場合は no-op で false を返す。
   */
  toggle: (input: LibraryFavoriteInput) => Promise<boolean>
  /**
   * ISBN で状態を更新する (楽観的更新)。
   * サーバ側で reserved/returned のタイムスタンプが自動セットされ、成功時はそのレスポンスで確定する。
   * `reserved` への遷移では予約先の図書館を渡せる (未指定なら館なしで保存する)。
   * ISBN が正規化できない・お気に入りに無い場合は no-op で false を返す。
   */
  updateStatus: (isbn: string, status: LibraryFavoriteStatus, library?: ReservedLibrary | null) => Promise<boolean>
  /**
   * その図書館の受取可の本をまとめて受け取ったことにする (受取 TODO もサーバ側で完了になる)。
   * 受け取っても status は reserved のまま。インポートした時点で returned に移す。
   */
  receivePickups: (libraryId: number) => Promise<boolean>
}

const NO_PICKUP = {
  pickup_state: null,
  pickup_deadline: null,
  pickup_branch: null,
  picked_up_at: null,
} as const satisfies Pick<LibraryFavorite, 'pickup_state' | 'pickup_deadline' | 'pickup_branch' | 'picked_up_at'>

/**
 * 楽観的更新での予約館の値を決める。サーバ側の分岐と揃える。
 * - reserved: 渡された館で上書きする (未指定なら館なしの予約)
 * - wishlist: 予約が無かったことになるのでクリアする
 * - returned: どこで借りたかの履歴として残す
 */
const resolveOptimisticLibrary = (
  prev: LibraryFavorite,
  status: LibraryFavoriteStatus,
  library: ReservedLibrary | null | undefined,
): Pick<LibraryFavorite, 'reserved_library_id' | 'reserved_library_name'> => {
  if (status === 'reserved') {
    return { reserved_library_id: library?.id ?? null, reserved_library_name: library?.name ?? null }
  }
  if (status === 'wishlist') return { reserved_library_id: null, reserved_library_name: null }
  return { reserved_library_id: prev.reserved_library_id, reserved_library_name: prev.reserved_library_name }
}

export const useLibraryFavoritesStore = create<LibraryFavoritesState>()((set, get) => ({
  byIsbn: {},
  order: [],
  loaded: false,
  loading: false,
  error: null,

  loadFavorites: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const favorites = await listLibraryFavorites()
      const byIsbn: Record<string, LibraryFavorite> = {}
      const order: string[] = []
      for (const fav of favorites) {
        const key = normalizeIsbn(fav.isbn)
        if (!key) continue
        byIsbn[key] = fav
        order.push(key)
      }
      set({ byIsbn, order, loaded: true, loading: false })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'お気に入りの取得に失敗しました'
      set({ loading: false, error: message })
    }
  },

  isFavorite: (isbn) => {
    const key = normalizeIsbn(isbn)
    if (!key) return false
    return key in get().byIsbn
  },

  toggle: async (input) => {
    const key = normalizeIsbn(input.isbn)
    if (!key) return false

    const state = get()
    const currently = key in state.byIsbn

    if (currently) {
      // 楽観的削除
      const prev = state.byIsbn[key]
      const nextByIsbn = { ...state.byIsbn }
      delete nextByIsbn[key]
      set({
        byIsbn: nextByIsbn,
        order: state.order.filter((k) => k !== key),
      })
      try {
        await removeLibraryFavorite(key)
        return true
      } catch (e) {
        // ロールバック
        set({
          byIsbn: { ...get().byIsbn, [key]: prev },
          order: [key, ...get().order],
          error: e instanceof Error ? e.message : 'お気に入り解除に失敗しました',
        })
        return false
      }
    }

    // 楽観的追加 (仮レコード)。status は必ず 'wishlist' で開始する (サーバ側と揃える)。
    const optimistic: LibraryFavorite = {
      id: -1,
      isbn: key,
      title: input.title,
      author: input.author ?? null,
      publisher: input.publisher ?? null,
      pubdate: input.pubdate ?? null,
      cover: input.cover ?? null,
      status: 'wishlist',
      reserved_at: null,
      returned_at: null,
      reserved_library_id: null,
      reserved_library_name: null,
      ...NO_PICKUP,
      created_at: new Date().toISOString(),
      product: null,
    }
    set({
      byIsbn: { ...state.byIsbn, [key]: optimistic },
      order: [key, ...state.order],
    })
    try {
      const created = await addLibraryFavorite(input)
      // サーバから返った id / created_at で置き換える
      set({
        byIsbn: { ...get().byIsbn, [key]: created },
      })
      return true
    } catch (e) {
      // ロールバック
      const rollback = { ...get().byIsbn }
      delete rollback[key]
      set({
        byIsbn: rollback,
        order: get().order.filter((k) => k !== key),
        error: e instanceof Error ? e.message : 'お気に入り追加に失敗しました',
      })
      return false
    }
  },

  updateStatus: async (isbn, status, library) => {
    const key = normalizeIsbn(isbn)
    if (!key) return false
    const state = get()
    const prev = state.byIsbn[key]
    if (!prev) return false
    // 同じ reserved でも予約先の館だけを変えるケースがあるため、status 一致だけでは打ち切らない。
    const libraryId = library?.id ?? null
    if (prev.status === status && (status !== 'reserved' || prev.reserved_library_id === libraryId)) return true

    // 楽観的更新: reserved/returned への遷移で、まだ空のタイムスタンプがあればローカルにも仮値を入れる。
    // (サーバ側で厳密な値が確定するので、成功時はレスポンスで置き換える)
    const nowIso = new Date().toISOString()
    const optimistic: LibraryFavorite = {
      ...prev,
      status,
      reserved_at: status === 'reserved' && prev.reserved_at == null ? nowIso : prev.reserved_at,
      returned_at: status === 'returned' && prev.returned_at == null ? nowIso : prev.returned_at,
      ...resolveOptimisticLibrary(prev, status, library),
      // 予約し直し・候補に戻すと前回の受取情報はサーバ側で消える。インポート済み (returned) では残る
      ...(status === 'returned' ? {} : NO_PICKUP),
    }
    set({ byIsbn: { ...state.byIsbn, [key]: optimistic } })

    try {
      const updated = await updateLibraryFavoriteStatus(key, status, library)
      // レスポンス反映時にも、間で remove された可能性を考慮する。
      const latest = get().byIsbn
      if (!(key in latest)) return true
      set({ byIsbn: { ...latest, [key]: updated } })
      return true
    } catch (e) {
      // ロールバック: リクエスト中に remove されている可能性があるので、
      // 削除済みならロールバック対象がないため復活させない (race condition 回避)。
      const latest = get().byIsbn
      const message = e instanceof Error ? e.message : '状態の更新に失敗しました'
      if (!(key in latest)) {
        set({ error: message })
        return false
      }
      set({
        byIsbn: { ...latest, [key]: prev },
        error: message,
      })
      return false
    }
  },

  receivePickups: async (libraryId) => {
    try {
      const received = await receiveLibraryPickups(libraryId)
      const next = { ...get().byIsbn }
      for (const fav of received) {
        const key = normalizeIsbn(fav.isbn)
        if (key && key in next) next[key] = fav
      }
      set({ byIsbn: next })
      return true
    } catch (e) {
      set({ error: e instanceof Error ? e.message : '受け取りの記録に失敗しました' })
      return false
    }
  },
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末で図書館お気に入りが追加/更新/削除されたらリストを再読み込みする。
registerSyncListener((event) => {
  if (
    event.type !== 'library_favorite.created' &&
    event.type !== 'library_favorite.updated' &&
    event.type !== 'library_favorite.deleted'
  ) {
    return
  }
  if (event.origin === CLIENT_ID) return

  // 差分計算 (isbn ベース) は困難なので素直に全件再取得。
  // loadFavorites 側で loading 中の場合は no-op になる。
  void useLibraryFavoritesStore.getState().loadFavorites()
})
